import { forwardRef } from 'react';
import { withStyles } from '../../../utils';
import styles from './SwitchIndicator.module.scss';

const cn = withStyles(styles);

type SwitchIndicatorProps = {
  checked?: boolean;
  className?: string;
  disabled?: boolean;
  /** Shows a value the user cannot change: an on switch stays recognisably on, where a disabled one is grey. */
  readOnly?: boolean;
};

/**
 * Visual-only; has no interactivity on its own.
 *
 * Put an `input[type="checkbox"]` AFTER it if you want it to be a checkbox:
 * ```tsx
 * <label>
 *   <SwitchIndicator checked={checked} />
 *   <input type="checkbox" onChange={toggleChecked} />
 * </label>
 * ```
 *
 * Wrap it in a button if you want it to be a button:
 * ```tsx
 * <button onClick={toggleChecked}>
 *   <SwitchIndicator checked={checked} />
 * </button>
 * ```
 *
 * Or it can just be a visual representation of some other thing being activated:
 * ```tsx
 * <button onClick={toggleChecked}>Toggle It</button>
 * ...
 * <SwitchIndicator checked={checked} />
 * ```
 */
const SwitchIndicator = forwardRef<HTMLSpanElement, SwitchIndicatorProps>(
  ({ checked = false, className, disabled = false, readOnly = false }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'switchIndicator',
          checked && 'checked',
          disabled && 'disabled',
          readOnly && 'readOnly',
          className,
        )}
      />
    );
  },
);

export default SwitchIndicator;
